<script lang="ts">
  /// 会員 (公開サイトのマイページ登録者) 一覧テーブル (U10)。読み取り専用の純表示ウィジェット。
  ///
  /// FSD: widget は判断を持たず props でデータだけを受け取る。権限判定と取得はページの責務。
  ///
  /// 列は出所で 2 群に分け、見出し行でそれを示す。左が Auth0 のルート属性 (CRM が読める確定
  /// 情報)、右が D20 プロフィール契約の user_metadata (会員本人の申告)。申告は CRM のマスタ
  /// ではないので、同じ群に並べて確定情報と誤読させない。
  ///
  /// Auth0 群を左に置くのは、9/22 時点の会員がほぼ「登録しただけで未完了」= 申告欄が空に
  /// なるため。その状態でも行を識別できるのはメールだけなので、識別子を左に置く。
  ///
  /// member_kind と litigation_cases_per_month は列にしない。D20 の表にはあるが書き手である
  /// mypage が登録項目に持たず、v1 では全員 null になるため (裁定で戻れば列を足すだけ)。
  import type { SiteMember, SiteMemberProfile } from '@domain/site-member/model';
  import { formatDateYmd } from '@shared/util/format';

  interface Props {
    members: SiteMember[];
  }

  const { members }: Props = $props();

  const BLANK = '—';

  function text(value: string | null): string {
    return value ?? BLANK;
  }

  /// 姓名は片方だけの申告もありうる (mypage の保存は差分 PATCH)。埋まっている方だけを出す。
  function fullName(profile: SiteMemberProfile): string {
    const parts = [profile.last_name, profile.first_name].filter(
      (part): part is string => part !== null && part !== '',
    );
    return parts.length === 0 ? BLANK : parts.join(' ');
  }

  function legalTrainingTerm(profile: SiteMemberProfile): string {
    return profile.legal_training_term === null ? BLANK : `${profile.legal_training_term} 期`;
  }

  function loginsCount(member: SiteMember): string {
    return member.logins_count === null ? BLANK : String(member.logins_count);
  }

  function termsTitle(profile: SiteMemberProfile): string {
    const version = profile.terms_version === null ? '版の記録なし' : `版 ${profile.terms_version}`;
    return `利用規約に同意 (${formatDateYmd(profile.terms_consented_at)} / ${version})`;
  }

  function hasNoConsent(profile: SiteMemberProfile): boolean {
    return profile.terms_consented_at === null && profile.marketing_consented_at === null;
  }
</script>

<div class="table-scroll">
  <table class="data-table">
    <thead>
      <tr class="group-row">
        <th colspan="5" scope="colgroup" class="group group-verified">
          Auth0 (CRM が読める確定情報)
        </th>
        <th colspan="6" scope="colgroup" class="group group-declared">
          本人の申告 (マイページ入力)
        </th>
      </tr>
      <tr>
        <th scope="col">メール</th>
        <th scope="col">確認</th>
        <th scope="col">登録日</th>
        <th scope="col">最終ログイン</th>
        <th scope="col">ログイン</th>
        <th scope="col" class="declared declared-start">氏名</th>
        <th scope="col" class="declared">組織名</th>
        <th scope="col" class="declared">電話</th>
        <th scope="col" class="declared">修習期</th>
        <th scope="col" class="declared">同意</th>
        <th scope="col" class="declared">登録状態</th>
      </tr>
    </thead>
    <tbody>
      {#if members.length === 0}
        <tr class="empty-row">
          <td colspan="11">
            <div class="empty-state">
              <p class="empty">まだ会員がいません</p>
              <p class="empty-hint">
                公開サイトのマイページから会員登録が行われると、ここに表示されます。
              </p>
            </div>
          </td>
        </tr>
      {:else}
        {#each members as member (member.user_id)}
          <tr>
            <td>{text(member.email)}</td>
            <td>
              {#if member.email_verified}
                <span class="badge verified">済</span>
              {:else}
                <span class="badge unverified" title="メールアドレスが未確認です">未</span>
              {/if}
            </td>
            <td>{formatDateYmd(member.created_at)}</td>
            <td>
              {#if member.last_login === null}
                <span class="muted">未ログイン</span>
              {:else}
                {formatDateYmd(member.last_login)}
              {/if}
            </td>
            <td>{loginsCount(member)}</td>
            <td class="declared declared-start">{fullName(member.profile)}</td>
            <td class="declared">{text(member.profile.organization_name)}</td>
            <td class="declared">{text(member.profile.phone)}</td>
            <td class="declared">{legalTrainingTerm(member.profile)}</td>
            <td class="declared">
              {#if hasNoConsent(member.profile)}
                {BLANK}
              {:else}
                {#if member.profile.terms_consented_at !== null}
                  <span class="badge consent" title={termsTitle(member.profile)}>規約</span>
                {/if}
                {#if member.profile.marketing_consented_at !== null}
                  <span
                    class="badge consent"
                    title={`メール配信に同意 (${formatDateYmd(member.profile.marketing_consented_at)})`}
                  >
                    配信
                  </span>
                {/if}
              {/if}
            </td>
            <td class="declared">
              {#if member.profile.profile_completed_at === null}
                <span class="badge incomplete" title="初回プロフィールの登録が完了していません">
                  未完了
                </span>
              {:else}
                {formatDateYmd(member.profile.profile_completed_at)}
              {/if}
            </td>
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>

<style>
  /* 11 列 + 長い組織名で panel の幅を超えるため、表だけを横スクロールさせる
     (LawyerTable / ProductTable と同じ作法)。 */
  .table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table th,
  .data-table td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  .data-table th {
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }
  /* 出所の群見出し。境目が分かるように左罫線を入れる。 */
  .group {
    padding-top: 10px;
    padding-bottom: 6px;
    font-size: 11px;
    letter-spacing: 0.02em;
  }
  .group-verified {
    color: #1f2937;
  }
  .group-declared {
    color: #a16207;
  }
  .group-row th + th,
  .declared-start {
    border-left: 1px solid var(--border);
  }
  /* 申告列は薄い地色を敷き、確定情報と視覚的にも分ける。 */
  .declared {
    background: rgba(234, 179, 8, 0.04);
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .empty-row td {
    border-bottom: none;
  }
  .empty-state {
    padding: 32px 0;
    text-align: center;
    white-space: normal;
  }
  .empty {
    margin: 0;
    color: var(--muted, #6b7280);
    font-weight: 600;
  }
  .empty-hint {
    margin: 6px 0 0;
    color: var(--muted, #6b7280);
    font-size: 12px;
  }
  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
  }
  .badge + .badge {
    margin-left: 4px;
  }
  .verified {
    background: rgba(34, 197, 94, 0.15);
    color: #15803d;
  }
  .unverified {
    background: rgba(148, 163, 184, 0.2);
    color: #475569;
  }
  .consent {
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
  }
  .incomplete {
    background: rgba(234, 179, 8, 0.15);
    color: #a16207;
  }
</style>
